interface ResolveIconHrefOptions {
    readonly symbolId?: string;
}

type IconAssetDirectory = "badge-icons" | "large-icons" | "system-icons";

const storybookBaseUrl = normalizeBaseUrl("/img");
let configuredBaseUrl = "";

function normalizeBaseUrl(baseUrl: string): string {
    const trimmedBaseUrl = baseUrl.trim();

    if (!trimmedBaseUrl || trimmedBaseUrl === "/") {
        return "";
    }

    let end = trimmedBaseUrl.length;
    while (end > 0 && trimmedBaseUrl[end - 1] === "/") {
        end--;
    }
    return trimmedBaseUrl.slice(0, end);
}

function getConfiguredBaseUrl(): string {
    if (!configuredBaseUrl) {
        throw new Error(
            'Icon assets base URL is not configured. Call IconAssets.configure("/path") during app bootstrap.'
        );
    }

    return configuredBaseUrl;
}

function trimSlashes(segment: string): string {
    let start = 0;
    let end = segment.length;
    while (start < end && segment[start] === "/") {
        start++;
    }
    while (end > start && segment[end - 1] === "/") {
        end--;
    }
    return segment.slice(start, end);
}

function joinPath(baseUrl: string, ...segments: string[]): string {
    const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
    const normalizedSegments = segments.map(trimSlashes);

    return [normalizedBaseUrl, ...normalizedSegments].filter(Boolean).join("/");
}

/**
 * Centralizes runtime configuration and URL resolution for design-system icon assets.
 */
function configure(baseUrl: string): void {
    const normalizedBaseUrl = normalizeBaseUrl(baseUrl);

    if (!normalizedBaseUrl) {
        throw new Error("Icon assets base URL cannot be empty.");
    }

    configuredBaseUrl = normalizedBaseUrl;
}

function isStorybook(): boolean {
    return configuredBaseUrl === storybookBaseUrl;
}

function resolveUrl(directory: IconAssetDirectory, assetName: string): string {
    return joinPath(getConfiguredBaseUrl(), directory, assetName);
}

function resolveHref(
    directory: Exclude<IconAssetDirectory, "large-icons">,
    iconName: string,
    options: ResolveIconHrefOptions = {}
): string {
    const assetUrl = resolveUrl(directory, `${iconName}.svg`);

    return options.symbolId ? `${assetUrl}#${options.symbolId}` : assetUrl;
}

const iconAssets = {
    configure,
    isStorybook,
    resolveUrl,
    resolveHref,
};

export { iconAssets as IconAssets };
